import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticMSB } from "./realistic";

/**
 * MSBNode – Main Switchboard visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with moving analog meter needles & phase pilot lamps
 * - Premium glassmorphism layout
 * - Classic industrial SVG layout
 */
const MSBNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticMSB
              liveValue={liveValue}
              unit={unit || "V"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

        // Animation speed — 0 when offline or stopped, scaled near maxLevel
        const pulseDuration = isRunning ? `${Math.max(0.4, 1.5 / (speedScale || 1))}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="82"
            viewBox="0 0 80 82"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  {/* Steel panel gradient */}
                  <linearGradient id="msbChassis" x1="0" y1="0" x2="80" y2="70" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#334155" />
                    <stop offset="50%" stopColor="#475569" />
                    <stop offset="100%" stopColor="#1e293b" />
                  </linearGradient>
                  {/* Busbar gradient */}
                  <linearGradient id="copperBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#b45309" />
                    <stop offset="50%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#78350f" />
                  </linearGradient>
                  <filter id="electricityGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="1.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Outer cabinet shadow */}
                <rect x="3" y="5" width="74" height="60" rx="4" fill="#0f172a" opacity="0.4" />

                {/* Metal panel casing */}
                <rect x="4" y="4" width="72" height="60" rx="4" fill="url(#msbChassis)" stroke="#64748b" strokeWidth="1.5" />

                {/* Cabinet door divide lines */}
                <line x1="24" y1="4" x2="24" y2="64" stroke="#1e293b" strokeWidth="1" />
                <line x1="4" y1="20" x2="76" y2="20" stroke="#1e293b" strokeWidth="1" />

                {/* Left Side: Control & Display Section */}
                {/* Warning label */}
                <polygon points="14,8 8,18 20,18" fill="#fbbf24" stroke="#000" strokeWidth="0.5" />
                <path d="M14,11 L14,15 M14,16.5 L14,17.2" stroke="#000" strokeWidth="1" strokeLinecap="round" />

                {/* Digital voltmeter screen */}
                <rect x="7" y="24" width="14" height="8" rx="1" fill="#020617" stroke={isRunning ? "#22c55e" : "#334155"} strokeWidth="1" />
                <text x="14" y="30.5" fill="#22c55e" fontSize="6.5" fontFamily="monospace" fontWeight="extrabold" textAnchor="middle" filter={isRunning ? "url(#electricityGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.4;1" dur="1s" repeatCount="indefinite" />
                  )}
                </text>

                {/* Status LEDs (Red, Green, Yellow) */}
                <circle cx="8" cy="38" r="1.5" fill={isRunning ? "#b91c1c" : "#451a03"} />
                <circle cx="14" cy="38" r="1.5" fill={isRunning ? "#22c55e" : "#022c22"} filter={isRunning ? "url(#electricityGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.4;1" dur="1s" repeatCount="indefinite" />
                  )}
                </circle>
                <circle cx="20" cy="38" r="1.5" fill={isRunning ? "#d97706" : "#422006"} />

                {/* Cooling vents at the bottom */}
                <line x1="7" y1="46" x2="21" y2="46" stroke="#0f172a" strokeWidth="1" />
                <line x1="7" y1="49" x2="21" y2="49" stroke="#0f172a" strokeWidth="1" />
                <line x1="7" y1="52" x2="21" y2="52" stroke="#0f172a" strokeWidth="1" />

                {/* Right Side: Busbars & Distribution breakers */}
                {/* Copper busbars behind switches */}
                <rect x="30" y="24" width="4" height="36" fill="url(#copperBar)" opacity={isRunning ? "1" : "0.5"} />
                <rect x="48" y="24" width="4" height="36" fill="url(#copperBar)" opacity={isRunning ? "1" : "0.5"} />
                <rect x="66" y="24" width="4" height="36" fill="url(#copperBar)" opacity={isRunning ? "1" : "0.5"} />

                {/* Pulsing energy background glow behind busbars */}
                {isRunning && (
                  <rect x="26" y="22" width="48" height="40" fill="#fef08a" opacity="0.15" filter="url(#electricityGlow)">
                    <animate attributeName="opacity" values="0.05;0.25;0.05" dur="1.5s" repeatCount="indefinite" />
                  </rect>
                )}

                {/* Animated current waves on busbars when running */}
                {isRunning && (
                  <>
                    <line x1="32" y1="24" x2="32" y2="60" stroke="#fef08a" strokeWidth="3.5" strokeDasharray="6 8" filter="url(#electricityGlow)">
                      <animate attributeName="stroke-dashoffset" values="0;28" dur="0.8s" repeatCount="indefinite" />
                    </line>
                    <line x1="50" y1="24" x2="50" y2="60" stroke="#fef08a" strokeWidth="3.5" strokeDasharray="6 8" filter="url(#electricityGlow)">
                      <animate attributeName="stroke-dashoffset" values="14;42" dur="0.8s" repeatCount="indefinite" />
                    </line>
                    <line x1="68" y1="24" x2="68" y2="60" stroke="#fef08a" strokeWidth="3.5" strokeDasharray="6 8" filter="url(#electricityGlow)">
                      <animate attributeName="stroke-dashoffset" values="7;35" dur="0.8s" repeatCount="indefinite" />
                    </line>
                  </>
                )}

                {/* Breaker Modules */}
                {[
                  { x: 28, y: 28 }, { x: 28, y: 46 },
                  { x: 46, y: 28 }, { x: 46, y: 46 },
                  { x: 64, y: 28 }, { x: 64, y: 46 }
                ].map((b, idx) => (
                  <g key={idx}>
                    {/* Breaker switchbox */}
                    <rect x={b.x} y={b.y} width="8" height="10" rx="1" fill="#1e293b" stroke="#334155" strokeWidth="0.5" />
                    {/* Toggle switch position */}
                    <line
                      x1={b.x + 4} y1={b.y + (isRunning ? 2 : 8)}
                      x2={b.x + 4} y2={b.y + (isRunning ? 8 : 2)}
                      stroke={isRunning ? "#22c55e" : "#ef4444"}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </g>
                ))}

                {/* Premium status dot */}
                <circle cx="70" cy="10" r="5" fill={dotColor} stroke="#ffffff" strokeWidth="1.5" filter={isRunning ? "url(#electricityGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>

                {/* Status text */}
                <text x="40" y="76" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
                  {statusColor === "green" ? "ENERGIZED" : statusColor === "red" ? "OFF" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* MSB panel */}
                <rect
                  x="5" y="5" width="70" height="50" rx="3"
                  fill={isRunning ? `${color}15` : `${color}08`}
                  stroke={isRunning ? color : "#64748b"}
                  strokeWidth="2"
                />

                {/* MSB label */}
                <text x="40" y="16" textAnchor="middle" fontSize="9" fontWeight="bold" fill={color}>MSB</text>

                {/* Bus bars (3 horizontal lines) */}
                <line x1="10" y1="24" x2="70" y2="24" stroke={color} strokeWidth="2.5" />
                <line x1="10" y1="32" x2="70" y2="32" stroke={color} strokeWidth="2.5" />
                <line x1="10" y1="40" x2="70" y2="40" stroke={color} strokeWidth="2.5" />

                {/* Circuit breaker indicators */}
                {[20, 35, 50, 65].map((x) => (
                  <g key={x}>
                    <rect
                      x={x - 3} y="22" width="6" height="20" rx="1"
                      fill={isRunning ? `${color}30` : `${color}10`}
                      stroke={color}
                      strokeWidth="1"
                    />
                    {isRunning && (
                      <circle
                        cx={x} cy="32" r="2"
                        fill={dotColor}
                        style={{ animation: `live-electrical-pulse ${pulseDuration} ease-in-out infinite` }}
                      />
                    )}
                  </g>
                ))}

                {/* Power pulse indicator */}
                {isRunning && (
                  <g style={{ animation: `live-electrical-pulse ${pulseDuration} ease-in-out infinite` }}>
                    <line x1="10" y1="32" x2="18" y2="32" stroke="#fbbf24" strokeWidth="3" opacity="0.6" />
                  </g>
                )}

                {/* Input/output */}
                <rect x="0" y="29" width="5" height="6" fill={color} rx="1" />
                <rect x="75" y="29" width="5" height="6" fill={color} rx="1" />

                {/* Status dot - top right */}
                <circle cx="70" cy="10" r="4" fill={dotColor} />
                <text x="40" y="76" textAnchor="middle" fontSize="7" fontWeight="600" fill={dotColor}>
                  {statusColor === "green" ? "ENERGIZED" : statusColor === "red" ? "OFF" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

MSBNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default MSBNode;
